import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { MessageSchema, MessageType } from "@common/types/chat";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { CountResponseSchema } from "@common/schemas/count-response.schema";
import { ChatReactionBodySchema } from "@common/schemas/chatReaction.schema";
import {
    ListThreadsResponseSchema,
    ListMessagesResponseSchema,
    ListSubthreadMessagesResponseSchema,
    GetOrCreateSubthreadResponseSchema,
    SendSubthreadMessageResponseSchema,
    ResolveChatMessageResponseSchema,
    ChatReactionMessageResponseSchema,
    ChatReactionPathParamsSchema,
    MarkAllChatMessagesReadResponseSchema,
    optionalSpreadsheetReferencesSchema,
    optionalTransactionReferencesSchema,
    optionalCounterpartyReferencesSchema,
    optionalDocumentReferencesSchema,
    optionalInvoiceReferencesSchema,
    optionalFinancialDocumentIdsSchema,
} from "@common/schemas/chat.schema";

export {
    ListThreadsResponseSchema,
    ListMessagesResponseSchema,
    ListSubthreadMessagesResponseSchema,
    GetOrCreateSubthreadResponseSchema,
    SendSubthreadMessageResponseSchema,
    ResolveChatMessageResponseSchema,
};

const c = initContract();

export const CHAT_MESSAGE_CONTENT_MAX_LENGTH = 100000;

export type ListThreadsResponse = z.infer<typeof ListThreadsResponseSchema>;

export const chatContract = c.router(
    {
        getThreads: {
            method: "GET",
            path: "/:workspaceId/threads",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: z.object({
                limit: z.coerce
                    .number()
                    .int()
                    .min(1)
                    .max(100)
                    .default(20)
                    .optional(),
                cursor: z.string().optional(), // opaque cursor
                organizationId: z.string().uuid().optional(),
            }),
            responses: {
                200: ListThreadsResponseSchema,
                403: ErrorMessageSchema,
            },
        },
        getMessages: {
            method: "GET",
            path: "/:workspaceId/thread/:threadId/messages",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
            }),
            query: z.object({
                limit: z.coerce
                    .number()
                    .int()
                    .min(1)
                    .max(200)
                    .default(50)
                    .optional(),
                cursor: z.string().optional(), // opaque cursor
                organizationId: z.string().uuid().optional(),
            }),
            responses: {
                200: ListMessagesResponseSchema,
                403: ErrorMessageSchema,
            },
        },
        getNumberOfUnreadMessages: {
            method: "GET",
            path: "/:workspaceId/messages/unread",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: z.object({
                organizationId: z.string().uuid().optional(),
            }),
            responses: {
                200: CountResponseSchema,
                403: ErrorMessageSchema,
            },
        },
        markAllRead: {
            method: "POST",
            path: "/:workspaceId/messages/read",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: z.object({
                organizationId: z.string().uuid().optional(),
            }),
            body: c.noBody(),
            summary:
                "Mark every unread message the caller can see in the workspace as read",
            responses: {
                200: MarkAllChatMessagesReadResponseSchema,
                403: ErrorMessageSchema,
            },
        },
        sendMessage: {
            method: "POST",
            path: "/:workspaceId/thread/:threadId/messages",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
            }),
            body: z.object({
                content: z.string().min(1).max(CHAT_MESSAGE_CONTENT_MAX_LENGTH),
                type: z.nativeEnum(MessageType).optional(),
                organizationId: z.string().uuid().optional(),
                spreadsheetReferences: optionalSpreadsheetReferencesSchema,
                transactionReferences: optionalTransactionReferencesSchema,
                counterpartyReferences: optionalCounterpartyReferencesSchema,
                documentReferences: optionalDocumentReferencesSchema,
                invoiceReferences: optionalInvoiceReferencesSchema,
                financialDocumentIds: optionalFinancialDocumentIdsSchema,
            }),
            responses: {
                200: z.object({
                    message: MessageSchema,
                }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        markRead: {
            method: "POST",
            path: "/:workspaceId/thread/:threadId/readReceipts",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        setReadReceipt: {
            method: "PATCH",
            path: "/:workspaceId/thread/:threadId/readReceipts",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
            }),
            body: z.object({
                readAt: z
                    .string()
                    .datetime()
                    .refine((val) => new Date(val) <= new Date(), {
                        message: "readAt must not be in the future",
                    }),
            }),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateMessage: {
            method: "PATCH",
            path: "/:workspaceId/thread/:threadId/messages/:messageId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
                messageId: z.string().uuid(),
            }),
            body: z.object({
                content: z.string().min(1).max(CHAT_MESSAGE_CONTENT_MAX_LENGTH),
            }),
            responses: {
                200: z.object({
                    message: MessageSchema,
                }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        resolveMessage: {
            method: "POST",
            path: "/:workspaceId/thread/:threadId/messages/:messageId/resolve",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
                messageId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: ResolveChatMessageResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        unresolveMessage: {
            method: "POST",
            path: "/:workspaceId/thread/:threadId/messages/:messageId/unresolve",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
                messageId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: ResolveChatMessageResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        addReaction: {
            method: "POST",
            path: "/:workspaceId/thread/:threadId/messages/:messageId/reactions",
            pathParams: ChatReactionPathParamsSchema,
            body: ChatReactionBodySchema,
            responses: {
                200: ChatReactionMessageResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        removeReaction: {
            method: "POST",
            path: "/:workspaceId/thread/:threadId/messages/:messageId/reactions/remove",
            pathParams: ChatReactionPathParamsSchema,
            body: ChatReactionBodySchema,
            responses: {
                200: ChatReactionMessageResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteMessage: {
            method: "DELETE",
            path: "/:workspaceId/thread/:threadId/messages/:messageId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
                messageId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getOrCreateSubthread: {
            method: "POST",
            path: "/:workspaceId/thread/:threadId/messages/:messageId/subthread",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                threadId: z.string().uuid(),
                messageId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: GetOrCreateSubthreadResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getSubthreadMessages: {
            method: "GET",
            path: "/:workspaceId/subthread/:subthreadId/messages",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                subthreadId: z.string().uuid(),
            }),
            query: z.object({
                limit: z.coerce
                    .number()
                    .int()
                    .min(1)
                    .max(200)
                    .default(50)
                    .optional(),
                cursor: z.string().optional(),
                organizationId: z.string().uuid().optional(),
            }),
            responses: {
                200: ListSubthreadMessagesResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        sendSubthreadMessage: {
            method: "POST",
            path: "/:workspaceId/subthread/:subthreadId/messages",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                subthreadId: z.string().uuid(),
            }),
            body: z.object({
                content: z.string().min(1).max(CHAT_MESSAGE_CONTENT_MAX_LENGTH),
                type: z.nativeEnum(MessageType).optional(),
                organizationId: z.string().uuid().optional(),
                spreadsheetReferences: optionalSpreadsheetReferencesSchema,
                transactionReferences: optionalTransactionReferencesSchema,
                counterpartyReferences: optionalCounterpartyReferencesSchema,
                documentReferences: optionalDocumentReferencesSchema,
                invoiceReferences: optionalInvoiceReferencesSchema,
                financialDocumentIds: optionalFinancialDocumentIdsSchema,
            }),
            responses: {
                200: SendSubthreadMessageResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/chat",
    },
);
